import { Chip } from '@mui/material';
import { flatMap, map, sortBy } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CopyTextId,
  DataGrid,
  DataGridColumnDefinition,
  DataWrapper,
  DateLabel,
  Dialog,
  dataGridColumns,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { PayoutManualBatch, PayoutManualBatchItem } from 'types';
import { orderUtils } from 'utils';

/** One manual attempt on this order, flattened out of the batch it belonged to. */
type ManualAttempt = PayoutManualBatchItem & {
  operatorName?: string;
  operatorRole?: string;
  providerName?: string;
  operation: PayoutManualBatch['operation'];
};

type Props = {
  queryResult: UseQueryResult<PayoutManualBatch[]>;
  open: boolean;
  onClose: () => void;
};

export const PayoutManualHistoryDialog: React.FC<Props> = ({
  queryResult,
  open,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.manual',
  });

  const attempts: ManualAttempt[] = useMemo(
    () =>
      sortBy(
        flatMap(queryResult.data, (batch) =>
          map(batch.items, (item) => ({
            ...item,
            operatorName: batch.user?.name,
            operatorRole: batch.user?.role,
            providerName: batch.p2pProvider?.name,
            operation: batch.operation,
          })),
        ),
        'createdAt',
      ),
    [queryResult.data],
  );

  const columns: DataGridColumnDefinition<ManualAttempt>[] = useMemo(
    () => [
      dataGridColumns.getIdColumn<ManualAttempt>(),
      {
        header: t('history.columns.operator'),
        valueGetter: (item) => (
          <Fragment>
            <div>{item.operatorName || '—'}</div>
            {item.operatorRole && <DateLabel>{item.operatorRole}</DateLabel>}
          </Fragment>
        ),
      },
      {
        header: t('history.columns.operation'),
        valueGetter: (item) => t(`batches.operations.${item.operation}` as any),
      },
      {
        header: t('history.columns.provider'),
        valueGetter: (item) => item.providerName || '—',
      },
      {
        header: t('result_dialog.columns.result'),
        valueGetter: (item) => (
          <Chip
            size="small"
            variant="outlined"
            color={orderUtils.getManualBatchResultColor(item.result)}
            label={orderUtils.getManualBatchResultLabel(
              item.result,
              item.operation,
            )}
          />
        ),
      },
      {
        header: t('batches.columns.status_change'),
        valueGetter: (item) =>
          `${orderUtils.getManualBatchStatusLabel(
            item.previousStatus,
            item.previousStatusDetails,
          )} → ${orderUtils.getManualBatchStatusLabel(
            item.resultStatus,
            item.resultStatusDetails,
          )}`,
      },
      {
        header: t('result_dialog.columns.reason'),
        valueGetter: (item) => orderUtils.getManualBatchItemReason(item) || '—',
      },
      {
        header: t('batches.columns.provider_request_id'),
        valueGetter: (item) =>
          item.providerRequestId ? (
            <CopyTextId id={item.providerRequestId} />
          ) : (
            '—'
          ),
      },
    ],
    [t],
  );

  const handleClose = useCallback(() => onClose(), [onClose]);

  return (
    <Dialog
      open={open}
      title={t('history_title')}
      hideCancel
      maxWidth="lg"
      onClose={handleClose}
    >
      <DataWrapper queryResult={queryResult}>
        <DataGrid columns={columns} data={attempts} />
      </DataWrapper>
    </Dialog>
  );
};
